import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { AlertTriangle, Clock, X, ChevronRight } from "lucide-react";
import { formatINR } from "@/lib/format";

const SESSION_KEY = "overdue_seen_at";

function isoToday() {
  return new Date().toISOString().slice(0, 10);
}

export default function OverdueAlerts() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [bannerOpen, setBannerOpen] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/overdue");
        if (cancelled) return;
        setData(data);
        const overdueCount =
          (data.totals?.rent_overdue_count || 0) + (data.totals?.gst_overdue_count || 0);
        // Show modal once per browser session (per day) when there's anything actually overdue
        const seenAt = sessionStorage.getItem(SESSION_KEY);
        if (overdueCount > 0 && seenAt !== isoToday()) {
          setModalOpen(true);
          sessionStorage.setItem(SESSION_KEY, isoToday());
        }
      } catch {
        /* silently ignore - this is just an alert layer */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  if (!data) return null;

  const { totals = {}, rent_overdue = [], gst_overdue = [], due_soon = [] } = data;
  const overdueCount = (totals.rent_overdue_count || 0) + (totals.gst_overdue_count || 0);
  const overdueAmount = (totals.rent_overdue_amount || 0) + (totals.gst_overdue_amount || 0);
  const soonCount = totals.due_soon_count || 0;

  const allOverdue = [...rent_overdue, ...gst_overdue];
  const hasOverdue = overdueCount > 0;
  const hasSoon = soonCount > 0;

  if (!hasOverdue && !hasSoon) return null;

  const goToProperty = (item) => {
    setModalOpen(false);
    navigate(`/property/${item.property_id}`);
  };

  return (
    <>
      {bannerOpen && (
        <div
          className={`mb-6 rounded-xl border ${
            hasOverdue
              ? "border-red-200 bg-red-50/80"
              : "border-amber-200 bg-amber-50/80"
          } px-5 py-4 flex items-start gap-4`}
          data-testid="overdue-banner"
        >
          <div
            className={`shrink-0 h-10 w-10 rounded-lg ${
              hasOverdue ? "bg-red-600" : "bg-amber-500"
            } text-white flex items-center justify-center`}
          >
            {hasOverdue ? (
              <AlertTriangle className="h-5 w-5" strokeWidth={1.5} />
            ) : (
              <Clock className="h-5 w-5" strokeWidth={1.5} />
            )}
          </div>
          <div className="flex-1 min-w-0">
            {hasOverdue ? (
              <>
                <p className="text-lg sm:text-xl font-bold text-red-900">
                  {overdueCount} overdue · ₹{formatINR(Math.round(overdueAmount))} pending
                </p>
                <p className="text-base font-semibold text-red-800 mt-1">
                  {totals.rent_overdue_count > 0 && (
                    <span>
                      {totals.rent_overdue_count} rent · ₹
                      {formatINR(Math.round(totals.rent_overdue_amount || 0))}
                    </span>
                  )}
                  {totals.rent_overdue_count > 0 && totals.gst_overdue_count > 0 && (
                    <span> · </span>
                  )}
                  {totals.gst_overdue_count > 0 && (
                    <span>
                      {totals.gst_overdue_count} GST · ₹
                      {formatINR(Math.round(totals.gst_overdue_amount || 0))}
                    </span>
                  )}
                  {hasSoon && (
                    <span className="text-stone-700"> · {soonCount} due in 7 days</span>
                  )}
                </p>
              </>
            ) : (
              <>
                <p className="text-lg sm:text-xl font-bold text-amber-900">
                  {soonCount} item{soonCount > 1 ? "s" : ""} due in the next 7 days
                </p>
                <p className="text-base font-semibold text-amber-800 mt-1">
                  Nothing overdue yet — earliest{" "}
                  <span className="font-bold">{due_soon[0]?.tenant_name || "—"}</span> on{" "}
                  <span className="font-bold">{due_soon[0]?.due_date}</span>
                </p>
              </>
            )}
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setModalOpen(true)}
              className={hasOverdue ? "text-red-900 hover:bg-red-100" : "text-amber-900 hover:bg-amber-100"}
              data-testid="overdue-view-all"
            >
              View all
            </Button>
            <button
              onClick={() => setBannerOpen(false)}
              aria-label="Dismiss"
              className="text-stone-400 hover:text-stone-700 p-1.5 rounded-md"
              data-testid="overdue-dismiss"
            >
              <X className="h-4 w-4" strokeWidth={1.5} />
            </button>
          </div>
        </div>
      )}

      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-hidden flex flex-col" data-testid="overdue-modal">
          <DialogHeader>
            <DialogTitle className="text-2xl font-light tracking-tight flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-red-600" strokeWidth={1.5} />
              Outstanding payments
            </DialogTitle>
            <DialogDescription className="text-base font-medium text-stone-700">
              Items past their due date or coming up soon. Click a row to open that property.
            </DialogDescription>
          </DialogHeader>

          <div className="overflow-y-auto -mx-6 px-6 mt-2 space-y-6">
            {allOverdue.length > 0 && (
              <Section title="Overdue" tone="red">
                {allOverdue.map((item) => (
                  <Row key={`${item.kind}-${item.rent_id}`} item={item} onClick={() => goToProperty(item)} />
                ))}
              </Section>
            )}
            {due_soon.length > 0 && (
              <Section title="Due in next 7 days" tone="amber">
                {due_soon.map((item) => (
                  <Row key={`${item.kind}-${item.rent_id}`} item={item} onClick={() => goToProperty(item)} />
                ))}
              </Section>
            )}
            {allOverdue.length === 0 && due_soon.length === 0 && (
              <p className="text-sm text-stone-500 py-8 text-center">All clear — nothing pending.</p>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

function Section({ title, tone, children }) {
  const dot = tone === "red" ? "bg-red-500" : "bg-amber-500";
  return (
    <div>
      <div className="flex items-center gap-2 mb-3">
        <span className={`h-2.5 w-2.5 rounded-full ${dot}`} />
        <h4 className="text-sm font-bold uppercase tracking-[0.18em] text-stone-700">
          {title}
        </h4>
      </div>
      <div className="rounded-lg border border-stone-200 bg-white divide-y divide-stone-200">
        {children}
      </div>
    </div>
  );
}

function Row({ item, onClick }) {
  const isGst = item.kind === "gst" || item.kind === "gst_due_soon";
  const isOverdue = item.days_overdue > 0;
  return (
    <button
      onClick={onClick}
      className="w-full flex items-center gap-4 px-5 py-4 hover:bg-stone-50 transition-colors text-left"
      data-testid={`overdue-row-${item.rent_id}-${item.kind}`}
    >
      <div className="flex-1 min-w-0">
        <p className="text-base font-bold text-stone-900 truncate">
          {item.tenant_name || "—"}{" "}
          <span className="text-stone-600 font-semibold">· {item.property_name}</span>
        </p>
        <p className="text-sm font-semibold text-stone-700 mt-1">
          {isGst ? "GST" : "Rent"} for {item.month_label} · due {item.due_date}
          {isOverdue ? (
            <span className="text-red-700 font-bold"> · {item.days_overdue}d overdue</span>
          ) : (
            <span className="text-amber-700 font-bold"> · in {Math.abs(item.days_overdue)}d</span>
          )}
        </p>
      </div>
      <div className="text-right shrink-0">
        <p className="text-base font-bold text-stone-900">₹{formatINR(Math.round(item.amount))}</p>
        <p className="text-xs font-semibold uppercase tracking-wider text-stone-500 mt-0.5">{isGst ? "GST" : "Rent"}</p>
      </div>
      <ChevronRight className="h-5 w-5 text-stone-400" strokeWidth={1.5} />
    </button>
  );
}
